/**
 * AboutSection — Morgan Legal Group
 * Design: Asymmetric split — image left (law library), content right
 * Gold accent details, attorney credentials
 */
import { useEffect, useRef, useState } from "react";
import { Award, BookOpen, MapPin, CheckCircle, Star } from "lucide-react";

const practiceAreas = [
  "Estate Planning",
  "Probate",
  "Wills & Trusts",
  "Asset Protection",
  "Guardianship",
  "Power of Attorney",
  "Elder Law",
  "Family Law",
  "Matrimonial",
  "Real Estate",
  "Personal Injury",
];

export default function AboutSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="py-20 lg:py-28 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left — Images */}
          <div
            className={`relative transition-all duration-700 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
            }`}
          >
            {/* Main panel — law library backdrop */}
            <div className="relative w-full h-[420px] lg:h-[520px] bg-navy overflow-hidden">
              {/* The poster carries the frame until the section is actually reached.
                  autoPlay downloads the whole file no matter what `preload` says,
                  and this clip is 2.5MB — a quarter of the homepage's weight spent
                  below the fold. The source is attached only once `visible` flips. */}
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="none"
                poster="/img/sections/about-video-poster.webp"
                className="absolute inset-0 w-full h-full object-cover"
              >
                {visible && <source src="/video/about-russel-morgan.mp4" type="video/mp4" />}
              </video>
              {/* Subtle gold tone overlay */}
              <div className="absolute inset-0 bg-gradient-to-tr from-navy/30 via-transparent to-gold/[0.04] pointer-events-none" />
              {/* Bottom overlay for depth */}
              <div className="absolute inset-0 bg-gradient-to-t from-navy/40 via-transparent to-transparent pointer-events-none" />
              {/* Gold frame accent */}
              <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-gold/40 pointer-events-none" />
            </div>

            {/* Founder name tag */}
            <div className="absolute -bottom-6 -left-4 lg:-left-8 bg-navy p-4 shadow-2xl flex items-center gap-4 max-w-[280px]">
              <div>
                <p className="font-display text-white text-base font-bold">Russel Morgan</p>
                <p className="font-body text-gold text-xs tracking-wide uppercase font-medium">CEO & Founder</p>
                <div className="flex items-center gap-1 mt-1">
                  {[1,2,3,4,5].map(i => (
                    <span key={i} className="text-gold text-xs">★</span>
                  ))}
                  <span className="font-body text-white/60 text-xs ml-1">4.9</span>
                </div>
              </div>
            </div>

            {/* Experience badge */}
            <div className="absolute top-6 -right-4 lg:-right-6 bg-gold p-4 shadow-xl text-center max-w-[120px]">
              <p className="font-display text-navy text-lg font-bold uppercase leading-tight">Seasoned</p>
              <p className="font-body text-navy text-xs font-semibold uppercase tracking-wide mt-1">NY Counsel</p>
            </div>
          </div>

          {/* Right — Content */}
          <div
            className={`transition-all duration-700 delay-200 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
            }`}
          >
            <div className="section-label mb-4">About Our Firm</div>
            <h2 className="font-display text-navy text-3xl sm:text-4xl lg:text-4xl xl:text-5xl font-bold leading-tight mb-3">
              A Top Rated{" "}
              <span className="text-gold italic">New York Estate Planning</span>{" "}
              Law Firm
            </h2>
            {/* «Top Rated» — утверждение проверяемое, но только вместе с основанием:
                рядом стоит сам рейтинг, к которому оно привязано. Прежнее «Most
                Trusted» основания не имело вовсе. */}
            <div className="flex items-center gap-2 mb-5">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className="w-4 h-4 fill-gold text-gold" />
              ))}
              <span className="font-body text-navy/70 text-sm">
                4.9 average client rating
              </span>
            </div>

            <p className="font-body text-navy/70 text-base leading-relaxed mb-4">
              Russel Morgan is the principal of The Morgan Legal Group. Prior to forming his own private practice and throughout his legal career, Russel has concentrated in commercial and contract litigation, trust and estate planning, business and personal asset protection, wealth preservation and dynasty succession planning.
            </p>
            <p className="font-body text-navy/70 text-base leading-relaxed mb-6">
              Russel has also assisted clients with all forms of commercial and personal transactional work, including the preparation, re-structuring and litigation of various types of contracts. His comprehensive approach ensures every client receives a strategy as unique as their circumstances.
            </p>

            {/* Credentials */}
            <div className="mb-8">
              <h4 className="font-serif-elegant text-navy text-lg font-semibold mb-4">Practice Areas</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {practiceAreas.map((area) => (
                  <div key={area} className="flex items-start gap-2.5">
                    <CheckCircle className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                    <span className="font-body text-navy/75 text-sm">{area}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Highlights */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              {[
                { icon: Award, label: "900+ Reviews", sub: "Across All Platforms" },
                { icon: BookOpen, label: "100+ Years", sub: "Combined NY Experience" },
                { icon: MapPin, label: "New York State", sub: "Statewide Service" },
              ].map(({ icon: Icon, label, sub }) => (
                <div key={label} className="text-center p-3 bg-cream border border-gold/20">
                  <Icon className="w-5 h-5 text-gold mx-auto mb-2" />
                  <p className="font-body text-navy text-xs font-semibold">{label}</p>
                  <p className="font-body text-navy/50 text-xs">{sub}</p>
                </div>
              ))}
            </div>

            <button
              onClick={() => {
                const el = document.querySelector("#consultation");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className="btn-navy"
            >
              Schedule Your Consultation
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
